import {
  CommonSelectors,
  Constants,
  doubleClickZoom,
  moveFeatures
} from "@/libs/map-draw/lib-utils";
import {
  clearAllHelpers,
  getTCBaseFeatures,
  undraggableTypes,
  nearestPointOnLine
} from "@/libs/map-draw/helpers";

export const createTCSimpleSelectMode = (map, MapboxDraw, trafficCounts) => {
  const TCSimpleSelect = {};
  const customState = {
    previousCoords: null
  };

  TCSimpleSelect.onMouseOut = function(state) {
    // As soon as you mouse leaves the canvas, update the feature
    if (state.dragMoving) return this.fireUpdate();
  };

  TCSimpleSelect.startOnActiveFeature = function(state, e) {
    // Stop any already-underway extended interactions
    this.stopExtendedInteractions(state);

    // Disable map.dragPan immediately so it can't start
    this.map.dragPan.disable();

    // Re-render it and enable drag move
    this.doRender(e.featureTarget.properties.id);

    // Set up the state for drag moving
    state.canDragMove =
      undraggableTypes.indexOf(trafficCounts.geom_type) === -1;
    state.dragMoveLocation = e.lngLat;
  };

  TCSimpleSelect.clickOnFeature = function(state, e) {
    // Stop everything
    doubleClickZoom.disable(this);
    this.stopExtendedInteractions(state);

    const isShiftClick = CommonSelectors.isShiftDown(e);
    const selectedFeatureIds = this.getSelectedIds();
    const featureId = e.featureTarget.properties.id;
    const isFeatureSelected = this.isSelected(featureId);

    customState.previousCoords = e.featureTarget.geometry.coordinates;

    clearAllHelpers(this.map);

    // Click (without shift) on any selected feature but a point
    if (
      !isShiftClick &&
      isFeatureSelected &&
      this.getFeature(featureId).type !== Constants.geojsonTypes.POINT
    ) {
      // Enter direct select mode
      return this.changeMode(Constants.modes.DIRECT_SELECT, {
        featureId: featureId
      });
    }

    // Shift-click on a selected feature
    if (isFeatureSelected && isShiftClick) {
      // Deselect it
      this.deselect(featureId);
      this.updateUIClasses({ mouse: Constants.cursors.POINTER });
      if (selectedFeatureIds.length === 1) {
        doubleClickZoom.enable(this);
      }
      // Shift-click on an unselected feature
    } else if (!isFeatureSelected && isShiftClick) {
      // Add it to the selection
      this.select(featureId);
      this.updateUIClasses({ mouse: Constants.cursors.MOVE });
      // Click (without shift) on an unselected feature
    } else if (!isFeatureSelected && !isShiftClick) {
      // Make it the only selected feature
      selectedFeatureIds.forEach(id => this.doRender(id));
      this.setSelected(featureId);
      this.updateUIClasses({ mouse: Constants.cursors.MOVE });
    }

    trafficCounts.currentGCPoint = this.getFeature(featureId);
    trafficCounts.editingCallback = point => {
      this.map.fire(Constants.events.UPDATE, {
        action: "change_properties",
        features: [point.toGeoJSON()]
      });
    };
    if (trafficCounts.geom_type === "general_count_points") {
      trafficCounts.tcSettingsMode = "edit";
      map.toggleGcpSettings(true);
    }

    // No matter what, re-render the clicked feature
    this.doRender(featureId);
  };

  TCSimpleSelect.onTrash = function() {
    this.deleteFeature(this.getSelectedIds());
    this.fireActionable();
    map.toggleGcpSettings(false);
    trafficCounts.currentGCPoint = null;
  };

  TCSimpleSelect.clickAnywhere = function(state) {
    // Clear the re-render selection
    const wasSelected = this.getSelectedIds();
    if (wasSelected.length) {
      this.clearSelectedFeatures();
      wasSelected.forEach(id => this.doRender(id));
    }
    doubleClickZoom.enable(this);
    this.stopExtendedInteractions(state);

    if (trafficCounts.mode !== "fill") {
      map.toggleGcpSettings(false);
      trafficCounts.currentGCPoint = null;
    }
  };

  TCSimpleSelect.dragMove = function(state, e) {
    // Dragging when drag move is enabled
    state.dragMoving = true;
    e.originalEvent.stopPropagation();

    let delta;
    const selected = this.getSelected();
    const selectedFeature = selected[0];
    const { link_id } = selectedFeature.properties;

    // move count points
    const linksFeatures = getTCBaseFeatures(this.map, map, e, "links", 100);
    const filteredLinks = linksFeatures.filter(
      l => l.properties.id === link_id
    );

    if (filteredLinks.length) {
      const link = filteredLinks[0];
      const nearest = nearestPointOnLine(
        [e.lngLat.lng, e.lngLat.lat],
        link.geometry.coordinates
      );
      const { coordinates } = nearest.geometry;

      delta = {
        lng: coordinates[0] - state.dragMoveLocation.lng,
        lat: coordinates[1] - state.dragMoveLocation.lat
      };
      moveFeatures(this.getSelected(), delta);
    }

    const newFeature = this.getSelected();
    const newCoords = newFeature[0].coordinates;

    customState.previousCoords = newCoords;
    state.dragMoveLocation = {
      lng: newCoords[0],
      lat: newCoords[1]
    };
  };

  return { ...MapboxDraw.modes.simple_select, ...TCSimpleSelect };
};
